export const setupAudioAnalyzer = (
  stream: MediaStream,
  onLevelUpdate: (average: number) => void
) => {
  const audioContext = new AudioContext();
  const source = audioContext.createMediaStreamSource(stream);
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 256;
  source.connect(analyser);

  const dataArray = new Uint8Array(analyser.frequencyBinCount);
  const updateLevel = () => {
    analyser.getByteFrequencyData(dataArray);
    const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
    onLevelUpdate(average);
    requestAnimationFrame(updateLevel);
  };
  updateLevel();

  return () => {
    source.disconnect();
    audioContext.close();
  };
};

export const setupDataChannelHandlers = (
  dc: RTCDataChannel,
  setConnectionState: (state: ConnectionState) => void,
  setMessages: React.Dispatch<React.SetStateAction<TranscriptMessage[]>>,
  messages
) => {
  dc.onmessage = (e) => {
    const event = JSON.parse(e.data);

    if (event.type === 'session.created') {
      setConnectionState('connected');
      dc.send(
        JSON.stringify({
          type: 'session.update',
          session: {
            modalities: ['text', 'audio'],
            input_audio_format: 'pcm16',
            output_audio_format: 'pcm16',
            input_audio_transcription: {
              model: 'whisper-1',
            },
            turn_detection: {
              type: 'server_vad',
              silence_duration_ms: 1000,
            },
            temperature: 1,
          },
        })
      );
    }

    if (event.type === 'conversation.item.created') {
      setMessages([
        ...messages,
        { role: 'user', id: event.item.id, text: null },
      ]);
    }

    if (event.type === 'response.done') {
      const response = event.response;
      if (response.status === 'success') {
        const agentResponse = response.output[0];
        setMessages([
          messages,
          {
            role: 'assistant',
            id: event.response.id,
            text: agentResponse.content[0].transcript,
          },
        ]);
      }
    }

    if (
      event.type === 'conversation.item.input_audio_transcription.completed'
    ) {
      setMessages((prevMessages) => {
        return prevMessages.map((msg) => {
          if (msg.id === event.item_id) {
            return {
              ...msg,
              text: event.transcript,
            };
          }
          return msg;
        });
      });
    }

    if (event.type === 'transcript') {
      setMessages((prev) => {
        if (prev.some((msg) => msg.id === event.id)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: event.id,
            text: event.text,
            participantName: event.speaker,
            timestamp: Date.now(),
          },
        ];
      });
    }
  };
};

export const sendOffer = async (sdp: string, EPHEMERAL_KEY: string) => {
  const sdpResponse = await fetch('https://api.openai.com/v1/realtime/calls', {
    method: 'POST',
    body: sdp,
    headers: {
      Authorization: `Bearer ${EPHEMERAL_KEY}`,
      'Content-Type': 'application/sdp',
    },
  });

  return {
    type: 'answer' as RTCSdpType,
    sdp: await sdpResponse.text(),
  };
};

// conversation.item.input_audio_transcription.completed
